import { useState, useCallback, useRef, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AgGridColumn, AgGridReact } from "ag-grid-react";

import { Button } from "@material-ui/core";
import { CellValueChangedEvent, ColumnApi, GridApi, GridReadyEvent } from "ag-grid-community";

import { UIContext } from "../../../contexts";
import { ADMIN_DASHBOARD_HEADER } from "../../../constants/caption/dashboardHeader";

import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import getAgGridHeight from "../../../utils/commonFunctions/getAgGridHeight";
import { useEmployerRoute } from "../../../hooks";
import useRouteParam from "../../../hooks/useRouteParam";
import ExtractRouteParams from "../../../utils/commonFunctions/extractRouteParams";
import { dateTimeZoneConverter } from "../../../utils/commonFunctions/date";

const MemberInfoUpdate = (): JSX.Element => {
	const [agGridAPI, setAgGridAPI] = useState<GridApi | null>(null);
	const [agGridColumnAPI, setAgGridColumnAPI] = useState<ColumnApi | null>(null);
	const agGridRef = useRef<any>(null);
	const [isRowSelected, setIsRowSelected] = useState(false);
	const navigate = useNavigate();
	const isEmployerRoute = useEmployerRoute();
	const queryGroupNumber = useRouteParam("gn");
	const routeParam = ExtractRouteParams(); // This is to extract the route after ?(Question Mark)
	const { setDashboardHeader } = useContext(UIContext);
	const [gridHeight, setGridHeight] = useState("300px");
	const [fieldUpdateData, setFieldUpdateData] = useState<any[]>([
		{
			field_update_type: "Email Update",
			description: "This functionality will update the Email of Employee/Dependent of specific group."
		},
		{
			field_update_type: "SSN Update",
			description: "This functionality will update the SSN of Employee/Dependent of specific group."
		},
		{
			field_update_type: "Location Number Update",
			description: "This functionality will update the Location Number of Employee/Dependent of specific group."
		},
		{
			field_update_type: "Location Number Bulk Upload",
			description: "This functionality will upload the Location Number of Employee/Dependent of specific group."
		},
		{
			field_update_type: "Buy Up Eligibility Update",
			description: "This functionality will update the Buy Up Eligibility for Employee of specific group."
		},
		{
			field_update_type: "Hire Date Update",
			description: "This functionality will update the Hire Date for Employee/Dependent of specific group."
		},
		{
			field_update_type: "Date of Birth Update",
			description: "This functionality will update the Date of Birth for Employee/Dependent of specific group."
		}
	]);

	const handleAgGridReady = (params: GridReadyEvent) => {
		// eslint-disable-next-line no-unused-vars
		const { api, columnApi } = params;
		setAgGridAPI(api);
		setAgGridColumnAPI(columnApi);
		const columnIds: any[] = [];
		columnApi?.getAllColumns()?.forEach((column: any) => {
			columnIds.push(column.colId);
		});
		columnApi.autoSizeColumns(columnIds);
	};

	const handleFirstDataRendered = (params: any) => {
		params.api.sizeColumnsToFit();
		params.columnApi.setColumnWidth("action", "100%", true);
	};

	const handleFieldUpdateClick = useCallback(
		({
			_isEmployerRoute,
			_queryGroupNumber
		}: {
			_isEmployerRoute: boolean | null;
			_queryGroupNumber: number | null;
		}) => {
			if (agGridRef?.current) {
				const focusedCell = agGridRef.current.api.getFocusedCell();
				const row = agGridRef.current.api.getDisplayedRowAtIndex(focusedCell.rowIndex);
				const {
					data: { field_update_type }
				} = row;

				let pathname;

				if (field_update_type === "Email Update") {
					_isEmployerRoute === true && _queryGroupNumber !== null
						? (pathname = `/employer/member-info-update/email?${routeParam}`)
						: null;
				} else if (field_update_type === "SSN Update") {
					_isEmployerRoute === true && _queryGroupNumber !== null
						? (pathname = `/employer/member-info-update/SSN?${routeParam}`)
						: null;
				} else if (field_update_type === "Location Number Update") {
					_isEmployerRoute === true && _queryGroupNumber !== null
						? (pathname = `/employer/member-info-update/location-number?${routeParam}`)
						: null;
				} else if (field_update_type === "Location Number Bulk Upload") {
					_isEmployerRoute === true && _queryGroupNumber !== null
						? (pathname = `/employer/member-info-update/bulk-upload-location-number?${routeParam}`)
						: null;
				} else if (field_update_type === "Buy Up Eligibility Update") {
					_isEmployerRoute === true && _queryGroupNumber !== null
						? (pathname = `/employer/member-info-update/buy-up-eligibility?${routeParam}`)
						: null;
				} else if (field_update_type === "Hire Date Update") {
					_isEmployerRoute === true && _queryGroupNumber !== null
						? (pathname = `/employer/member-info-update/hire-date?${routeParam}`)
						: null;
				} else if (field_update_type === "Date of Birth Update") {
					_isEmployerRoute === true && _queryGroupNumber !== null
						? (pathname = `/employer/member-info-update/date-of-birth?${routeParam}`)
						: null;
				}

				navigate(pathname as string);
			}
		},
		[navigate, routeParam]
	);

	const handleActionButtonRender = ({
		_isEmployerRoute,
		_queryGroupNumber
	}: {
		_isEmployerRoute: boolean | null;
		_queryGroupNumber: number | null;
	}) => {
		return (
			<Button
				className="ag-grid-cell-button"
				variant="outlined"
				onClick={() =>
					handleFieldUpdateClick({
						_isEmployerRoute,
						_queryGroupNumber
					})
				}
			>
				Click to Update
			</Button>
		);
	};

	const handleSelectionChanged = useCallback(() => {
		const selectedRow = agGridAPI?.getSelectedRows()[0];
		if (selectedRow) {
			setIsRowSelected(true);
		} else {
			setIsRowSelected(false);
		}
	}, [agGridAPI]);

	const handleCellValueRender = ({ value }: { value: string }) => {
		return <div>{value ? <div>{value}</div> : <div style={{ marginLeft: 15 }}>N/A</div>}</div>;
	};

	const handleDateCellRenderer = ({ value }: { value: any }) => {
		const date = dateTimeZoneConverter(value as string) as string;
		return <div>{value ? date : "N/A"}</div>;
	};

	useEffect(() => {
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.member_info_update);
	}, [setDashboardHeader]);

	useEffect(() => {
		const _gridHeight = getAgGridHeight(fieldUpdateData.length);
		setGridHeight(_gridHeight);
	}, [fieldUpdateData.length]);

	return (
		<div className="group-home-page">
			<div className="group-home-pg-grid-container">
				<div className="ag-theme-alpine" style={{ height: gridHeight, width: "100%" }}>
					<AgGridReact
						ref={agGridRef}
						rowData={fieldUpdateData}
						onGridReady={handleAgGridReady}
						onFirstDataRendered={handleFirstDataRendered}
						rowSelection={"Single"}
						animateRows
						defaultColDef={{
							filter: "agTextColumnFilter",
							floatingFilter: true,
							resizable: true
						}}
						onSelectionChanged={handleSelectionChanged}
						frameworkComponents={{
							handleActionButtonRender: handleActionButtonRender,
							handleCellValueRender: handleCellValueRender,
							handleDateCellRenderer: handleDateCellRenderer
						}}
						onCellValueChanged={(params: CellValueChangedEvent) => {
							params.api.refreshCells({
								force: true,
								columns: ["branding"]
							});
						}}
						enableCellChangeFlash={true}
					>
						<AgGridColumn
							field="field_update_type"
							headerName="Field Update Type"
							floatingFilterComponentParams={{ suppressFilterButton: true }}
							// cellRenderer="handleCellValueRender"
						></AgGridColumn>
						<AgGridColumn
							field="description"
							headerName="Description"
							floatingFilterComponentParams={{ suppressFilterButton: true }}
							// cellRenderer="handleCellValueRender"
						></AgGridColumn>
						<AgGridColumn
							field="action"
							headerName="Action"
							cellRenderer="handleActionButtonRender"
							cellRendererParams={{
								_isEmployerRoute: isEmployerRoute,
								_queryGroupNumber: queryGroupNumber
							}}
							filter={false}
						></AgGridColumn>
					</AgGridReact>
				</div>
			</div>
		</div>
	);
};

export default MemberInfoUpdate;
